import { useEffect, useCallback, useRef } from 'react';

interface KeyboardShortcut {
  key: string;
  modifier?: 'ctrl' | 'cmd' | 'alt' | 'shift';
  action: () => void;
  description: string;
  preventDefault?: boolean;
}

interface UseKeyboardShortcutsProps {
  shortcuts: KeyboardShortcut[];
  enabled?: boolean;
}

export const useKeyboardShortcuts = ({
  shortcuts,
  enabled = true
}: UseKeyboardShortcutsProps) => {
  const shortcutsRef = useRef(shortcuts);
  
  // Update shortcuts ref when shortcuts change
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    // Don't trigger shortcuts when typing in input fields
    const target = event.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT' ||
      target.isContentEditable
    ) {
      return;
    }

    const matchingShortcut = shortcutsRef.current.find(shortcut => {
      const keyMatch = shortcut.key.toLowerCase() === event.key.toLowerCase();
      
      if (!keyMatch) return false;

      // Check modifier keys
      switch (shortcut.modifier) {
        case 'ctrl':
          return event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey;
        case 'cmd':
          return event.metaKey && !event.ctrlKey && !event.altKey && !event.shiftKey;
        case 'alt':
          return event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey;
        case 'shift':
          return event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey;
        default:
          return !event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey;
      }
    });

    if (matchingShortcut) {
      if (matchingShortcut.preventDefault !== false) {
        event.preventDefault();
      }
      matchingShortcut.action();
    }
  }, [enabled]);

  useEffect(() => {
    if (enabled) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [handleKeyDown, enabled]);

  return {
    shortcuts: shortcutsRef.current
  };
};

// Predefined conversation shortcuts
export const useConversationShortcuts = (actions: {
  onSearchFocus?: () => void;
  onNewConversation?: () => void;
  onToggleLeftPanel?: () => void;
  onToggleRightPanel?: () => void;
  onNextConversation?: () => void;
  onPrevConversation?: () => void;
  onArchiveConversation?: () => void;
  onStarConversation?: () => void;
  onMarkAsRead?: () => void;
  onReply?: () => void;
  onForward?: () => void;
  onOpenCommandPalette?: () => void;
  onRefresh?: () => void;
  onSelectAll?: () => void;
  onDeselectAll?: () => void;
}) => {
  const shortcuts: KeyboardShortcut[] = [
    // Search and navigation
    {
      key: '/',
      action: () => actions.onSearchFocus?.(),
      description: 'Focus search'
    },
    {
      key: 'k',
      modifier: 'cmd' as const,
      action: () => actions.onOpenCommandPalette?.(),
      description: 'Open command palette'
    },
    {
      key: 'n',
      modifier: 'cmd' as const,
      action: () => actions.onNewConversation?.(),
      description: 'New conversation'
    },
    
    // Panel controls
    {
      key: '[',
      modifier: 'cmd' as const,
      action: () => actions.onToggleLeftPanel?.(),
      description: 'Toggle left panel'
    },
    {
      key: ']',
      modifier: 'cmd' as const,
      action: () => actions.onToggleRightPanel?.(),
      description: 'Toggle right panel'
    },
    
    // Conversation navigation
    {
      key: 'j',
      action: () => actions.onNextConversation?.(),
      description: 'Next conversation'
    },
    {
      key: 'k',
      action: () => actions.onPrevConversation?.(),
      description: 'Previous conversation'
    },
    {
      key: 'ArrowDown',
      action: () => actions.onNextConversation?.(),
      description: 'Next conversation'
    },
    {
      key: 'ArrowUp',
      action: () => actions.onPrevConversation?.(),
      description: 'Previous conversation'
    },
    
    // Conversation actions
    {
      key: 'e',
      action: () => actions.onArchiveConversation?.(),
      description: 'Archive conversation'
    },
    {
      key: 's',
      action: () => actions.onStarConversation?.(),
      description: 'Star/unstar conversation'
    },
    {
      key: 'u',
      action: () => actions.onMarkAsRead?.(),
      description: 'Mark as read/unread'
    },
    {
      key: 'r',
      action: () => actions.onReply?.(),
      description: 'Reply to conversation'
    },
    {
      key: 'f',
      action: () => actions.onForward?.(),
      description: 'Forward conversation'
    },
    
    // General actions
    {
      key: 'r',
      modifier: 'cmd' as const,
      action: () => actions.onRefresh?.(),
      description: 'Refresh conversations'
    },
    {
      key: 'a',
      modifier: 'cmd' as const,
      action: () => actions.onSelectAll?.(),
      description: 'Select all conversations'
    },
    {
      key: 'Escape',
      action: () => actions.onDeselectAll?.(),
      description: 'Deselect all'
    }
  ].filter(shortcut => shortcut.action !== undefined);

  return useKeyboardShortcuts({ shortcuts });
};

// Helper function to format shortcut display
export const formatShortcut = (shortcut: KeyboardShortcut): string => {
  const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  
  let keys: string[] = [];
  
  if (shortcut.modifier) {
    switch (shortcut.modifier) {
      case 'cmd':
        keys.push(isMac ? '⌘' : 'Ctrl');
        break;
      case 'ctrl':
        keys.push('Ctrl');
        break;
      case 'alt':
        keys.push(isMac ? '⌥' : 'Alt');
        break;
      case 'shift':
        keys.push('⇧');
        break;
    }
  }
  
  // Format special keys
  let keyDisplay = shortcut.key;
  switch (shortcut.key.toLowerCase()) {
    case 'arrowup':
      keyDisplay = '↑';
      break;
    case 'arrowdown':
      keyDisplay = '↓';
      break;
    case 'arrowleft':
      keyDisplay = '←';
      break;
    case 'arrowright':
      keyDisplay = '→';
      break;
    case 'escape':
      keyDisplay = 'Esc';
      break;
    case 'enter':
      keyDisplay = '↵';
      break;
    case ' ':
      keyDisplay = 'Space';
      break;
    default:
      keyDisplay = shortcut.key.toUpperCase();
  }
  
  keys.push(keyDisplay);
  return keys.join(' + ');
}; 